import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {SlideItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {getScaledManualVerticalPadding} from '@PosterWhiteboard/items/item/item.library';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {TextSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';
import type * as Fabric from '@postermywall/fabricjs-2';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {addFonts, BULLETS_FONT_FAMILY, loadBulletsFont} from '@Libraries/font-library';
import {DEFAULT_FONT_SIZE} from '@PosterWhiteboard/page/add-item.class';
import {ITEM_CONTROL_DIMENSIONS} from '@PosterWhiteboard/poster/poster-item-controls';
import {DEFAULT_STROKE_WIDTH} from '@PosterWhiteboard/classes/text-styles.class';
import {createSlideItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import type {DeepPartial} from '@/global';
import type {SlideItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import type {TimelineResizeBounds, TimeSpan} from '@PosterWhiteboard/page/timeline-group-gestures.library';

export const MIN_INTRO_DELAY = 0;
export const MIN_SLIDE_DURATION = 1;
export const MAX_SLIDE_DURATION = 600;

interface SlideshowResizeBoundsData {
  isMultiSlide: boolean;
  firstSlideDuration: number;
  lastSlideDuration: number;
}

/**
 * Timeline resize bounds for a slideshow bar — the single definition both the group
 * gesture snapshot (from SlideshowItemObject data) and
 * SlideshowItem.getTimelineResizeBounds (class side) compute from, so a group
 * gesture's preview matches its commit. A slide-driven slideshow (multi-slide, or an
 * intro delay) resizes by growing/shrinking its FIRST slide (left handle) or LAST
 * slide (right handle), so each edge's travel is exactly that slide's remaining
 * headroom within [MIN_SLIDE_DURATION, MAX_SLIDE_DURATION]. A static-like slideshow
 * (single slide, no delay) is auto-fit to the page's visual span and
 * applyTimelineResize cannot move its span — a group resize leaves it frozen.
 */
export const getSlideshowTimelineResizeBounds = (span: TimeSpan, slideshow: SlideshowResizeBoundsData): TimelineResizeBounds => {
  const hasUserControlledDuration = slideshow.isMultiSlide || span.startTime > 0;
  if (!hasUserControlledDuration) {
    return {minStartTime: span.startTime, maxStartTime: span.startTime, maxEndTime: span.endTime, minEndTime: span.endTime};
  }
  return {
    minStartTime: Math.max(0, span.startTime - (MAX_SLIDE_DURATION - slideshow.firstSlideDuration)),
    maxStartTime: span.startTime + Math.max(0, slideshow.firstSlideDuration - MIN_SLIDE_DURATION),
    maxEndTime: span.endTime + (MAX_SLIDE_DURATION - slideshow.lastSlideDuration),
    minEndTime: span.endTime - Math.max(0, slideshow.lastSlideDuration - MIN_SLIDE_DURATION),
  };
};

export interface SlideshowSlidesObject {
  slidesHashMap: Record<string, SlideItemObject>;
  slidesOrder: string[];
}

interface SlideDimensions {
  width: number;
  height: number;
}

export class SlideshowSlides {
  public slidesHashMap: Record<string, SlideItem> = {};
  public slidesOrder: string[] = [];
  public slideshow: SlideshowItem;

  public constructor(slideshow: SlideshowItem) {
    this.slideshow = slideshow;
  }

  public async updateFromObject(
    slidesObject: DeepPartial<SlideshowSlidesObject> = {},
    {updateRedux = true, fireModifiedEvent = true, undoable = true, doInvalidate = true}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    // Update/Add items from slidesObject
    let areSlidesAddedOrRemoved = false;
    const updateFromObjectPromises = [];
    if (slidesObject.slidesHashMap) {
      const addItemPromises = [];

      for (const [uid, itemObject] of Object.entries(slidesObject.slidesHashMap)) {
        if (itemObject) {
          if (this.slidesHashMap[uid] !== undefined) {
            updateFromObjectPromises.push(
              this.slidesHashMap[uid].updateFromObject(itemObject, {
                updateRedux: false,
                fireModifiedEvent: false,
                undoable: false,
                doInvalidate,
              })
            );
          } else {
            addItemPromises.push(
              new Promise<void>((resolve, reject) => {
                showLoading(`addItem${uid}`);
                createSlideItemFromObject(this.slideshow.page, this.slideshow, itemObject)
                  .then(async (itemToAdd) => {
                    await this.scaleSlideIfNeeded(itemToAdd);
                    if (slidesObject.slidesOrder) {
                      await this.slideshow.addSlide(itemToAdd, slidesObject.slidesOrder.indexOf(itemToAdd.uid), {
                        updateRedux: false,
                        undoable: false,
                        fireEvent: false,
                      });
                      areSlidesAddedOrRemoved = true;
                    }
                    resolve();
                  })
                  .catch(reject)
                  .finally(() => {
                    hideLoading(`addItem${uid}`);
                  });
              })
            );
          }
        }
      }

      const responses = await Promise.allSettled(addItemPromises);
      for (const response of responses) {
        if (response.status === 'rejected') {
          if (this.slideshow.page.poster.mode.isGeneration()) {
            console.log('Slideshow slides failed to add details:', response.reason);
            throw response.reason;
          }
          console.error('Slideshow slides failed to add details:', response);
        }
      }
    }

    // delete items that are in this page but not in the slidesObject
    for (const [uid] of Object.entries(this.slidesHashMap)) {
      if (slidesObject.slidesHashMap?.[uid] === undefined) {
        areSlidesAddedOrRemoved = true;
        this.doRemoveItem(uid);
      }
    }

    if (areSlidesAddedOrRemoved && slidesObject.slidesOrder) {
      this.slidesOrder.sort((a, b) => (slidesObject.slidesOrder?.indexOf(a) ?? -1) - (slidesObject.slidesOrder?.indexOf(b) ?? -1));
    }

    await Promise.all(updateFromObjectPromises);
    await this.ensureFontsAreLoaded();

    if (undoable) {
      this.slideshow.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.slideshow.page.fire('modified');
    }
    if (updateRedux) {
      this.slideshow.page.poster.redux.updateReduxData();
    }
  }

  public getLastSlide(): SlideItem {
    return this.slidesHashMap[this.slidesOrder[this.slidesOrder.length - 1]];
  }

  protected async ensureFontsAreLoaded(): Promise<void> {
    await this.loadFontFamiliesForTextSlides();
    await this.loadBulletFontForTextSlides();
  }

  protected async loadFontFamiliesForTextSlides(): Promise<void> {
    const fontFamilies: string[] = [];
    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isTextSlide()) {
        const textSlideFontFamily = slide.textStyles.getFontFamilyToLoad();
        if (!fontFamilies.includes(textSlideFontFamily)) {
          fontFamilies.push(textSlideFontFamily);
        }
      }
    }

    if (fontFamilies.length > 0) {
      return new Promise((resolve, reject) => {
        addFonts(
          fontFamilies,
          () => {
            resolve();
          },
          reject
        );
      });
    }
    return Promise.resolve();
  }

  protected async loadBulletFontForTextSlides(): Promise<void> {
    if (this.doesTextSlidesHaveBulletFont()) {
      return new Promise((resolve) => {
        loadBulletsFont(BULLETS_FONT_FAMILY, () => {
          resolve();
        });
      });
    }
    return Promise.resolve();
  }

  protected doesTextSlidesHaveBulletFont(): boolean {
    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isTextSlide() && slide.list.hasList()) {
        return true;
      }
    }
    return false;
  }

  /**
   * Only scale the slide if it's a media slide, and it's the first slide in the slideshow.
   */
  private async scaleSlideIfNeeded(slideItem: SlideItem): Promise<void> {
    if ((this.slideshow.fabricObject.width === 0 || this.slideshow.fabricObject.height === 0) && slideItem.isMediaSlide()) {
      await this.slideshow.page.items.addItems.scaleItemToPosterDimension(slideItem);
    }
  }

  public moveSlideToIndex(slideId: string, newIndex: number): void {
    const newSlidesOrder = [...this.slidesOrder];
    const oldIndex = this.slidesOrder.indexOf(slideId);
    newSlidesOrder.splice(oldIndex, 1);
    newSlidesOrder.splice(newIndex, 0, slideId);

    this.slidesOrder = newSlidesOrder;
    this.seekToSlideIndex(newIndex);
    this.slideshow.restoreDefaultState();

    this.slideshow.page.poster.history.addPosterHistory();
    this.slideshow.page.poster.redux.updateReduxData();
  }

  public hasSlide(uid: string): boolean {
    return this.slidesHashMap[uid] !== undefined;
  }

  private doRemoveItem(itemUid: string): void {
    if (this.hasSlide(itemUid)) {
      this.slidesHashMap[itemUid].onRemove();
      this.slideshow.fabricObject.remove(this.slidesHashMap[itemUid].fabricObject as Fabric.Object);
      delete this.slidesHashMap[itemUid];
      this.removeSlideFromSlidesOrder(itemUid);

      if (this.slideshow.selectedSlideUID === itemUid) {
        this.slideshow.setSelectedSlide(this.getFirstSlide().uid);
      }
    }
  }

  private removeSlideFromSlidesOrder(slideId: string): void {
    const index = this.slidesOrder.indexOf(slideId);
    if (index > -1) {
      this.slidesOrder.splice(index, 1);
    }
  }

  public addSlideToHashmap(newSlide: SlideItem): void {
    this.slidesHashMap[newSlide.uid] = newSlide;
  }

  public toObject(): SlideshowSlidesObject {
    const slideObjects: Record<string, any> = {};

    for (const [key, slide] of Object.entries(this.slidesHashMap)) {
      slideObjects[key] = slide.toObject();
    }

    return {
      slidesHashMap: slideObjects,
      slidesOrder: [...this.slidesOrder],
    };
  }

  public getMaxDimensionsOfTextSlides(): SlideDimensions {
    let maxSlideWidth = 0;
    let maxSlideHeight = 0;
    let itemHeight = 0;
    let itemWidth = 0;
    let slideFabricObject;

    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isTextSlide()) {
        slideFabricObject = slide.fabricObject;

        // if new text slide isn't the first and only slide, take slideshow dimensions (don't want smaller new text slide to reduce overall slideshow dimensions)
        if (slide.isNewSlide && this.slideshow.fabricObject.width !== 0 && this.slideshow.fabricObject.height !== 0) {
          itemWidth = this.slideshow.fabricObject.width;
          itemHeight = this.slideshow.fabricObject.height;
        } else {
          itemWidth = slideFabricObject.getScaledWidth();
          itemHeight = slideFabricObject.getScaledHeight();
        }

        if (itemWidth > maxSlideWidth) {
          maxSlideWidth = itemWidth;
        }

        if (itemHeight > maxSlideHeight) {
          maxSlideHeight = itemHeight;
        }
      }
    }

    return {
      width: maxSlideWidth,
      height: maxSlideHeight,
    };
  }

  // TODO: See why these two params are being used and fix accordingly
  public updateSlideDimensions(): void {
    const slideShowWidth = this.slideshow.fabricObject.width;
    const slideShowHeight = this.slideshow.fabricObject.height;

    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isTextSlide()) {
        if (slide.isNewSlide) {
          slide.isNewSlide = false;
        }

        if (slide.fabricObject.getScaledWidth() !== slideShowWidth) {
          slide.fabricObject.set({
            width: slideShowWidth,
          });
          slide.updateTextChildWidth();
          // if (whiteboard) {
          //   slide.baseWidth = slide.fabricTextbox.width;
          //   void slide.updateFabricObject();
          // }
        }

        if (slide.fabricObject.getScaledHeight() !== slideShowHeight) {
          // if (updatedModel) {
          slide.verticalPadding = slideShowHeight - getScaledManualVerticalPadding(this.slideshow.fabricObject.scaleY) - slide.getTextStrokedHeight();
          // }
          slide.fabricObject.set({
            height: slideShowHeight,
          });
          slide.setBackgroundParams();
        }
      } else {
        this.scaleMediaSlide(slide.uid);
      }
    }
  }

  public scaleMediaSlide(slideId: string): void {
    if (!this.slideshow.page.poster.mode.isGeneration()) {
      const mediaSlide = this.slidesHashMap[slideId] as ImageSlideItem | VideoSlideItem;
      mediaSlide.mediaSlide.updateMediaSlideScale();
    }
  }

  public updateSlidePositions(): void {
    const slideShowWidth = this.slideshow.fabricObject.width;
    const slideShowHeight = this.slideshow.fabricObject.height;

    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isTextSlide()) {
        slide.fabricObject.set({
          left: -slideShowWidth / 2,
          top: -slideShowHeight / 2,
        });

        slide.setTextChildPosition();
      } else {
        slide.fabricObject.set(slide.mediaSlide.getMediaSlidePosition());
      }
    }
  }

  public isSlideEditable(slideId: string): boolean {
    const slideIndex = this.getSlideIndex(slideId);
    if (slideIndex !== undefined) {
      const slideModel = this.slidesHashMap[this.slidesOrder[slideIndex]];
      return slideModel.isTextSlide();
    }
    return false;
  }

  private isUnpurchasedGettyImageSlide(slide: SlideItem): slide is ImageSlideItem {
    return slide.isImageSlide() && slide.isNonPurchasedGettyImage();
  }

  private isGettyVideoSlide(slide: SlideItem): slide is VideoSlideItem {
    return slide.isVideoSlide() && slide.hasGettyContent();
  }

  public getUnpurchasedGettyImageSlideHashedFilenames(excludeItemUids?: Set<string>): string[] {
    const hashedFilenames: string[] = [];
    for (const [slideId, slide] of Object.entries(this.slidesHashMap)) {
      if (excludeItemUids?.has(slideId) || !this.isUnpurchasedGettyImageSlide(slide)) {
        continue;
      }
      const identityKey = slide.getGettyImageIdentityKey();
      if (identityKey) {
        hashedFilenames.push(identityKey);
      }
    }
    return hashedFilenames;
  }

  public hasVideoSlide(): boolean {
    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isVideoSlide()) {
        return true;
      }
    }
    return false;
  }

  public hasTextSlide(): boolean {
    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isTextSlide()) {
        return true;
      }
    }
    return false;
  }

  public hasMediaSlide(): boolean {
    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isMediaSlide()) {
        return true;
      }
    }
    return false;
  }

  public hasGettyVideoSlide(): boolean {
    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (this.isGettyVideoSlide(slide)) {
        return true;
      }
    }
    return false;
  }

  public getVideoSlides(): VideoSlideItem[] {
    const videoSlides: VideoSlideItem[] = [];
    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (slide.isVideoSlide()) {
        videoSlides.push(slide);
      }
    }
    return videoSlides;
  }

  public getGettyVideoSlideHashedFilenames(excludeItemUids?: Set<string>): string[] {
    const hashedFilenames: string[] = [];
    for (const [slideId, slide] of Object.entries(this.slidesHashMap)) {
      if (!excludeItemUids?.has(slideId) && this.isGettyVideoSlide(slide) && slide.hashedFilename) {
        hashedFilenames.push(slide.hashedFilename);
      }
    }
    return hashedFilenames;
  }

  public hasUnpurchasedGettyImageSlide(): boolean {
    for (const [, slide] of Object.entries(this.slidesHashMap)) {
      if (this.isUnpurchasedGettyImageSlide(slide)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether the slideshow item is premium or not
   */
  public isPremium(): boolean {
    return this.hasUnpurchasedGettyImageSlide() || this.hasGettyVideoSlide();
  }

  /**
   * Returns the last text slide if it exists
   */
  public getLastTextSlide(): TextSlideItem | undefined {
    for (let i = this.slidesOrder.length - 1; i >= 0; i--) {
      const slide = this.slidesHashMap[this.slidesOrder[i]];
      if (slide.isTextSlide()) {
        return slide;
      }
    }
    return undefined;
  }

  /**
   * Returns the duration of last media slide
   */
  public getLastMediaSlideDuration(): number | undefined {
    for (let i = this.slidesOrder.length - 1; i >= 0; i--) {
      const slide = this.slidesHashMap[this.slidesOrder[i]];
      if (slide.isMediaSlide()) {
        return slide.slideDuration;
      }
    }
    return undefined;
  }

  /**
   * Returns the page-relative time at which the slide starts playing.
   */
  public getStartTimeForSlide(slideId: string): number | undefined {
    let slideshowTime = this.slideshow.onPageStartTime;
    for (const id of this.slidesOrder) {
      if (slideId === id) {
        return slideshowTime;
      }
      slideshowTime += this.slidesHashMap[id].getSlideDuration();
    }
    return undefined;
  }

  /**
   * Returns the time of the poster when the slide ends after transitioning out.
   */
  public getEndTimeForSlide(slideId: string): number | undefined {
    const slideIndex = this.getSlideIndex(slideId);
    const startTimeForSlide = this.getStartTimeForSlide(slideId);
    if (startTimeForSlide !== undefined && slideIndex !== undefined) {
      return startTimeForSlide + this.slidesHashMap[this.slidesOrder[slideIndex]].getSlideDuration();
    }
    return undefined;
  }

  /**
   * Returns the time of poster for the slide after it has been transitioned in.
   */
  public getTimeForTransitionedSlide(slideId: string): number {
    // Don't add the transition delay duration if its the first slide with no intro delay and introOutroTransition option is false for it too
    return (
      (this.getStartTimeForSlide(slideId) ?? 0) +
      (this.getFirstSlide().uid !== slideId || this.slideshow.hasAnimation() || this.slideshow.hasIntroDelay() ? this.slideshow.getTransitionDuration() : 0)
    );
  }

  public isLastSlide(slideId: string): boolean {
    return this.getSlideIndex(slideId) === Object.keys(this.slidesHashMap).length - 1;
  }

  /**
   * Returns the slide index of the model for the time.
   */
  getSlideIndexForTime(time: number): number {
    const numberOfSlides = Object.keys(this.slidesHashMap).length;
    const slideShowDuration = this.slideshow.getDuration();
    let slideshowTime = this.slideshow.onPageStartTime;

    let index = 0;

    for (const id of this.slidesOrder) {
      const slide = this.slidesHashMap[id];
      slideshowTime += slide.getSlideDuration();
      if (slideshowTime > time) {
        return index;
      }
      if (time >= slideShowDuration) {
        return numberOfSlides - 1;
      }
      index += 1;
    }
    return index;
  }

  public getSlideIndex(slideId: string): number | undefined {
    const slideOrder = this.slidesOrder.indexOf(slideId);
    return slideOrder !== -1 ? slideOrder : undefined;
  }

  public getSlideForId(slideId: string): SlideItem | undefined {
    return this.slidesHashMap[slideId];
  }

  public isFirstSlideNonEmptyText(): boolean {
    const firstSlide = this.slidesHashMap[this.slidesOrder[0]];
    return firstSlide.isTextSlide() && !firstSlide.isTextEmpty();
  }

  public getFirstSlide(): SlideItem {
    return this.slidesHashMap[this.slidesOrder[0]];
  }

  public seekToSlideIndex(slideIndex: number): void {
    if (slideIndex !== undefined) {
      const selectedSlideTime = this.getTimeForTransitionedSlide(this.slidesHashMap[this.slidesOrder[slideIndex]].uid);
      void this.slideshow.page.seek(selectedSlideTime);
    }
  }

  public seekToSlideId(slideId: string): void {
    if (this.slidesHashMap[slideId]) {
      const selectedSlideTime = this.getTimeForTransitionedSlide(this.slidesHashMap[slideId].uid);
      void this.slideshow.page.seek(selectedSlideTime);
    }
  }

  /**
   * Returns the possible index of the slide after the currently selected slide.
   * @returns {number|null}
   */
  public getNextSlideIndex(): number | undefined {
    const nextSlideIndex = (this.getSlideIndex(this.slideshow.selectedSlideUID) ?? 0) + 1;
    return nextSlideIndex < Object.keys(this.slidesHashMap).length ? nextSlideIndex : undefined;
  }

  /**
   * Returns the possible index of the slide before the currently selected slide.
   */
  public getPreviousSlideIndex(): number | undefined {
    const previousSlideIndex = (this.getSlideIndex(this.slideshow.selectedSlideUID) ?? 0) - 1;
    return previousSlideIndex >= 0 ? previousSlideIndex : undefined;
  }

  /**
   * Returns true if the currently selected slide is not the last slide of the slideshow.
   */
  public hasNextSlide(): boolean {
    return this.getNextSlideIndex() !== undefined;
  }

  /**
   * Returns true if the currently selected slide is not the last slide of the slideshow.
   * @returns {boolean}
   */
  public hasPreviousSlide(): boolean {
    return this.getSlideIndex(this.slideshow.selectedSlideUID) !== 0;
  }

  /**
   * Sets the opacity of the first slide to 1.
   */
  public showFirstSlide(): void {
    this.getFirstSlide().fabricObject.set({opacity: 1});
  }

  public async getNewTextSlideItem(): Promise<SlideItem> {
    const widthOfThePoster = this.slideshow.page.poster.width;
    const heightOfThePoster = this.slideshow.page.poster.height;
    const fontSizeLowerLimit = widthOfThePoster > heightOfThePoster ? widthOfThePoster / DEFAULT_FONT_SIZE / 2 : heightOfThePoster / DEFAULT_FONT_SIZE / 2;
    const fontSize = Math.max(widthOfThePoster < heightOfThePoster ? widthOfThePoster / DEFAULT_FONT_SIZE : heightOfThePoster / DEFAULT_FONT_SIZE, fontSizeLowerLimit);

    return createSlideItemFromObject(this.slideshow.page, this.slideshow, {
      gitype: ITEM_TYPE.TEXTSLIDE,
      text: '',
      textStyles: {fontSize},
      width: this.slideshow.fabricObject.width,
      height: this.slideshow.fabricObject.height,
      baseWidth: this.slideshow.fabricObject.width - ITEM_CONTROL_DIMENSIONS.PMW_ITEM_LEGACY_PADDING * 2 - DEFAULT_STROKE_WIDTH,
      isNewSlide: true,
    });
  }

  public hasSingleSlide(): boolean {
    return this.getNumberOfSlides() === 1;
  }

  public getNumberOfSlides(): number {
    return Object.keys(this.slidesHashMap).length;
  }

  public getNumberOfTextSlides(): number {
    const slides = Object.values(this.slidesHashMap);
    let numberOfTextSlides = 0;
    for (const eachSlide of slides) {
      if (eachSlide.isTextSlide()) {
        numberOfTextSlides += 1;
      }
    }

    return numberOfTextSlides;
  }

  public getNumberOfImageSlides(): number {
    const slides = Object.values(this.slidesHashMap);
    let numberOfImageSlides = 0;
    for (const eachSlide of slides) {
      if (eachSlide.isImageSlide()) {
        numberOfImageSlides += 1;
      }
    }

    return numberOfImageSlides;
  }

  public getNumberOfVideoSlides(): number {
    const slides = Object.values(this.slidesHashMap);
    let numberOfVideoSlides = 0;
    for (const eachSlide of slides) {
      if (eachSlide.isVideoSlide()) {
        numberOfVideoSlides += 1;
      }
    }

    return numberOfVideoSlides;
  }
}

